import { FunctionComponent, SetStateAction, useContext, useState } from 'react'
import moment from 'moment';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { NumericFormat } from 'react-number-format';
import { IDiscardedImportAndIntegrationLines } from '../../../../modules/third-party-connection/shared-services/third-party-connection-import-line/third-party-connection-import-line.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { FormControl, useMediaQuery } from '@mui/material';
import gaiaTheme from '../../../../root-components/theme';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../form-field-components/supplier-picker';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { SourceLabel } from '../../miscellaneous-components/source-label';

export const EmissionsFinancialDiscardedTable: FunctionComponent<{
  discardedImportLines: IDiscardedImportAndIntegrationLines[],
  subsidiaries: ISubsidiary[],
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  allContactSuppliers: IContactSupplierDetails[],
  selectedSupplier: string,
  setSelectedSupplier: React.Dispatch<SetStateAction<any>>,
}> = ({ discardedImportLines,
  subsidiaries,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  setOrder,
  setOrderBy,
  allContactSuppliers,
  selectedSupplier,
  setSelectedSupplier,
}) => {
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const organisation = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [selected, setSelected] = useState<string[]>([]);
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const headCells: IHeadCell[] = [
      { id: 'contactName', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }), sortable: true },
      { id: 'source', numeric: false, label: 'Source', sortable: false },
      { id: 'groupName', numeric: false, label: 'Group', sortable: false },
      { id: 'spendBasedConversion', numeric: false, label: 'Spend Activity', sortable: true },
      // Only when something here was discarded by the system. A tab full of blanks for an
      // organisation whose discards are all manual would be noise.
      ...(discardedImportLines.some(line => line.skipReason)
        ? [{ id: 'skipReason', numeric: false, label: 'Why', sortable: false }]
        : []),
      { id: 'total', numeric: true, label: `Amount (${organisation?.currencyCode})`, sortable: true },
      { id: 'date', numeric: false, label: 'Date', sortable: true },
      ...(subsidiaries && (organisation?.enableSubsidiaries || subsidiaries.length > 1) ? [
        { id: 'subsidiary', numeric: false, label: screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' }), sortable: false },
      ] : []),
    ];
    const rows = discardedImportLines.map(line => {
      return {
        id: line.id,
        groupName: line.groupName,
        contactName: line.contactName,
        // The ledger it came from, or the spreadsheet import that staged it.
        source: <SourceLabel providerCode={line.providerCode} source={line.providerCode ? 'INTEGRATION' : 'FILE_IMPORT'} />,
        // A line excluded by a chart-of-accounts mapping deliberately carries no activity,
        // so there is nothing to render here. "Code null: null" read as corrupt data.
        spendBasedConversion: line.spendBasedConversionActivity
          ? (line.localAuthorityCustomIdentifier
            ? `Code ${line.localAuthorityCustomIdentifier}: ${line.spendBasedConversionActivity}`
            : line.spendBasedConversionActivity)
          : '',
        skipReason: line.skipReason || '',
        date: moment(line.date).format('DD/MM/YYYY'),
        total: (
          < NumericFormat value={line.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale />
        ),
        subsidiary: line.subsidiaryName,
      }
    });

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    return (
      <>
        <FormControl variant='outlined' sx={{ width: isMobileMode ? '100%' : '20%', mb: 2 }} size='small'>
          {/* Cleared means all suppliers. */}
          <SupplierPicker
            id='status'
            suppliers={allContactSuppliers}
            label='Filter'
            placeholder='All'
            size='small'
            value={selectedSupplier || null}
            onChange={(id) => setSelectedSupplier(id || '')}
          />
        </FormControl>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          onOrderChange={handleOrderChange}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
      </>
    )
  }